import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Input from '../../components/input'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'
import EventBus from '../../infrastructure/event-bus/event-bus'
import useI18n from '../../i18n/useI18n'
import { ValidationPatterns, ValidationMessages } from '../../utils/validation'
import RoleBasedAccess from '../../components/role-based-access'
import { UserTypeEnum } from '../../utils/enums'

const defaultForm = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  companyName: '',
  taxNumber: '',
  taxOffice: '',
  address: '',
  city: '',
  country: 'Turkey',
  postalCode: '',
  websiteUrl: '',
  logoUrl: '',
  commissionRate: '',
  licenseNumber: ''
}

const AgentFormPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = !!id
  const { t, i18n } = useI18n()

  const [formData, setFormData] = useState(defaultForm)
  const [validationErrors, setValidationErrors] = useState({})
  const [submitError, setSubmitError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadingAgent, setLoadingAgent] = useState(isEditMode)

  useEffect(() => {
    if (isEditMode && id) {
      fetchAgent()
    } else {
      setLoadingAgent(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode])

  const fetchAgent = () => {
    setLoadingAgent(true)
    dispatch(
      ApiCallActions.Get({
        url: `agents/${id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const agent = data?.data || data
          if (agent) {
            setFormData(mapAgentToForm(agent))
          }
          setLoadingAgent(false)
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('AGENTS_FORM_FETCH_ERROR'))
          setLoadingAgent(false)
          navigate('/agents')
        }
      })
    )
  }

  const mapAgentToForm = (agent = {}) => {
    const user = agent.user || {}
    return {
      email: user.email || agent.email || '',
      password: '', // Password is not included in edit mode
      firstName: user.firstName || agent.firstName || '',
      lastName: user.lastName || agent.lastName || '',
      phone: user.phone || agent.phone || '',
      companyName: agent.companyName || '',
      taxNumber: agent.taxNumber || '',
      taxOffice: agent.taxOffice || '',
      address: agent.address || '',
      city: agent.city || '',
      country: agent.country || 'Turkey',
      postalCode: agent.postalCode || '',
      websiteUrl: agent.websiteUrl || agent.website || '',
      logoUrl: agent.logoUrl || agent.logoURL || agent.logo || '',
      commissionRate: agent.commissionRate !== undefined && agent.commissionRate !== null ? agent.commissionRate.toString() : '',
      licenseNumber: agent.licenseNumber || '' // Not sent in PUT request, but kept for display
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setSubmitError('')

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const errors = {}

    // Email validation
    if (!formData.email || !formData.email.trim()) {
      errors.email = ValidationMessages.REQUIRED
    } else if (!ValidationPatterns.EMAIL.test(formData.email)) {
      errors.email = ValidationMessages.EMAIL
    }

    // Password validation (only required in create mode)
    if (!isEditMode) {
      if (!formData.password || !formData.password.trim()) {
        errors.password = ValidationMessages.REQUIRED
      } else if (!ValidationPatterns.PASSWORD_MIN.test(formData.password)) {
        errors.password = ValidationMessages.PASSWORD_MIN
      }
    }

    // First name validation
    if (!formData.firstName || !formData.firstName.trim()) {
      errors.firstName = ValidationMessages.REQUIRED
    } else if (!ValidationPatterns.NAME.test(formData.firstName.trim())) {
      errors.firstName = ValidationMessages.NAME
    }

    // Last name validation
    if (!formData.lastName || !formData.lastName.trim()) {
      errors.lastName = ValidationMessages.REQUIRED
    } else if (!ValidationPatterns.NAME.test(formData.lastName.trim())) {
      errors.lastName = ValidationMessages.NAME
    }

    // Company name validation
    if (!formData.companyName || !formData.companyName.trim()) {
      errors.companyName = ValidationMessages.REQUIRED
    }

    // Phone validation (removed)
    // if (formData.phone && formData.phone.trim()) {
    //   if (!ValidationPatterns.PHONE_GENERAL.test(formData.phone.trim())) {
    //     errors.phone = ValidationMessages.PHONE_GENERAL
    //   }
    // }

    // URL validations (optional but if provided, must be valid)
    if (formData.websiteUrl && formData.websiteUrl.trim()) {
      if (!ValidationPatterns.URL.test(formData.websiteUrl.trim())) {
        errors.websiteUrl = ValidationMessages.URL
      }
    }

    if (formData.logoUrl && formData.logoUrl.trim()) {
      if (!ValidationPatterns.URL.test(formData.logoUrl.trim())) {
        errors.logoUrl = ValidationMessages.URL
      }
    }

    // Commission rate validation (optional but if provided, must be valid number)
    if (formData.commissionRate && formData.commissionRate.trim()) {
      const commissionRate = parseFloat(formData.commissionRate)
      if (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100) {
        errors.commissionRate = t('AGENTS_FORM_VALIDATION_COMMISSION_RATE_INVALID')
      }
    }

    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      setSubmitError(t('AGENTS_FORM_VALIDATION_GENERIC'))
      return false
    }

    return true
  }

  const buildSubmitData = () => {
    const submitData = {
      email: formData.email.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      companyName: formData.companyName.trim()
    }

    // Password only in create mode
    if (!isEditMode) {
      submitData.password = formData.password
    }

    // Add optional fields (licenseNumber is not sent in PUT request per API docs)
    const optionalStringFields = ['phone', 'taxNumber', 'taxOffice', 'address', 'city', 'country', 'postalCode', 'websiteUrl', 'logoUrl']
    if (!isEditMode) {
      optionalStringFields.push('licenseNumber')
    }

    general.addOptionalFields(submitData, formData, {
      string: optionalStringFields,
      number: ['commissionRate']
    })

    return submitData
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitError('')

    if (!validateForm()) {
      return
    }

    const payload = buildSubmitData()
    setIsSubmitting(true)

    if (isEditMode) {
      dispatch(
        ApiCallActions.Put({
          url: `agents/${id}`,
          data: payload,
          query: { lang: i18n.language },
          showAlertOnError: true,
          showLoading: true,
          onSuccess: ({ data }) => {
            const apiData = data?.data || data
            const mergedEntityData = {
              id,
              ...formData,
              ...payload,
              ...apiData
            }

            EventBus.sc.onEntityUpdated({
              entityType: 'agent',
              entityId: id,
              entityData: mergedEntityData
            })

            general.messageSuccess(t('AGENTS_FORM_UPDATE_SUCCESS'))
            navigate('/agents')
          },
          onError: ({ errorMessage }) => {
            setSubmitError(errorMessage || t('AGENTS_FORM_UPDATE_ERROR'))
          },
          callback: () => setIsSubmitting(false)
        })
      )
    } else {
      dispatch(
        ApiCallActions.Post({
          url: 'agents',
          data: payload,
          query: { lang: i18n.language },
          showAlertOnError: true,
          showLoading: true,
          onSuccess: ({ data }) => {
            const entityData = data?.data || data

            if (entityData?.id) {
              EventBus.sc.onEntityCreated({
                entityType: 'agent',
                entityId: entityData.id,
                entityData
              })
            }

            general.messageSuccess(t('AGENTS_FORM_CREATE_SUCCESS'))
            navigate('/agents')
          },
          onError: ({ errorMessage }) => {
            setSubmitError(errorMessage || t('AGENTS_FORM_CREATE_ERROR'))
          },
          callback: () => setIsSubmitting(false)
        })
      )
    }
  }

  if (loadingAgent) {
    return (
      <section className="content">
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-12 text-center p-5">
              <p>{t('COMMON_LOADING')}</p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
      <section className="content">
        <div className="block-header">
          <div className="row">
            <div className="col-lg-7 col-md-6 col-sm-12 text-left">
              <h2>
                {isEditMode ? t('AGENTS_FORM_TITLE_EDIT') : t('AGENTS_FORM_TITLE_NEW')}
                <small>{isEditMode ? (formData.companyName || t('AGENTS_FORM_SUBTITLE_EDIT_LOADING')) : t('AGENTS_FORM_SUBTITLE_NEW')}</small>
              </h2>
            </div>
            <div className="col-lg-5 col-md-6 col-sm-12">
              <button
                className="btn btn-white btn-icon btn-round float-right m-l-10"
                type="button"
                onClick={() => navigate('/agents')}
                title={t('AGENTS_FORM_BACK')}
              >
                <i className="zmdi zmdi-arrow-left"></i>
              </button>
              <ul className="breadcrumb float-md-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">
                    <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/agents">{t('AGENTS_LIST_TITLE')}</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEditMode ? t('AGENTS_FORM_BREADCRUMB_EDIT') : t('AGENTS_FORM_BREADCRUMB_NEW')}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-lg-12">
              <div className="card">
                <div className="body" style={{ paddingTop: '20px' }}>
                  {submitError && (
                    <div className="alert alert-danger" role="alert">
                      {submitError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <h6 className="m-b-20">{t('AGENTS_FORM_SECTION_USER_INFO')}</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_EMAIL')} <span className="text-danger">*</span></label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_EMAIL_PLACEHOLDER')}
                            required
                          />
                          {validationErrors.email && <small className="text-danger">{validationErrors.email}</small>}
                        </div>
                      </div>
                      {!isEditMode && (
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>{t('AGENTS_FORM_FIELD_PASSWORD')} <span className="text-danger">*</span></label>
                            <Input
                              type="password"
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder={t('AGENTS_FORM_FIELD_PASSWORD_PLACEHOLDER')}
                              required
                            />
                            {validationErrors.password && <small className="text-danger">{validationErrors.password}</small>}
                          </div>
                        </div>
                      )}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_FIRST_NAME')} <span className="text-danger">*</span></label>
                          <Input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_FIRST_NAME_PLACEHOLDER')}
                            required
                          />
                          {validationErrors.firstName && <small className="text-danger">{validationErrors.firstName}</small>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_LAST_NAME')} <span className="text-danger">*</span></label>
                          <Input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_LAST_NAME_PLACEHOLDER')}
                            required
                          />
                          {validationErrors.lastName && <small className="text-danger">{validationErrors.lastName}</small>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_PHONE')}</label>
                          <Input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_PHONE_PLACEHOLDER')}
                          />
                          {validationErrors.phone && <small className="text-danger">{validationErrors.phone}</small>}
                        </div>
                      </div>
                    </div>

                    <hr className="m-t-30 m-b-30" />

                    <h6 className="m-b-20">{t('AGENTS_FORM_SECTION_COMPANY_INFO')}</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_COMPANY_NAME')} <span className="text-danger">*</span></label>
                          <Input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_COMPANY_NAME_PLACEHOLDER')}
                            required
                          />
                          {validationErrors.companyName && <small className="text-danger">{validationErrors.companyName}</small>}
                        </div>
                      </div>
                      {!isEditMode && (
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>{t('AGENTS_FORM_FIELD_LICENSE_NUMBER')}</label>
                            <Input
                              type="text"
                              name="licenseNumber"
                              value={formData.licenseNumber}
                              onChange={handleChange}
                              placeholder={t('AGENTS_FORM_FIELD_LICENSE_NUMBER_PLACEHOLDER')}
                            />
                          </div>
                        </div>
                      )}
                      {isEditMode && formData.licenseNumber && (
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>{t('AGENTS_FORM_FIELD_LICENSE_NUMBER')}</label>
                            <Input
                              type="text"
                              name="licenseNumber"
                              value={formData.licenseNumber}
                              disabled
                              placeholder={t('AGENTS_FORM_FIELD_LICENSE_NUMBER_PLACEHOLDER')}
                            />
                            <small className="text-muted">{t('AGENTS_FORM_LICENSE_NUMBER_NOTE')}</small>
                          </div>
                        </div>
                      )}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_TAX_NUMBER')}</label>
                          <Input
                            type="text"
                            name="taxNumber"
                            value={formData.taxNumber}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_TAX_NUMBER_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_TAX_OFFICE')}</label>
                          <Input
                            type="text"
                            name="taxOffice"
                            value={formData.taxOffice}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_TAX_OFFICE_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_COMMISSION_RATE')}</label>
                          <Input
                            type="number"
                            name="commissionRate"
                            value={formData.commissionRate}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_COMMISSION_RATE_PLACEHOLDER')}
                            min="0"
                            max="100"
                            step="0.1"
                          />
                          {validationErrors.commissionRate && <small className="text-danger">{validationErrors.commissionRate}</small>}
                        </div>
                      </div>
                    </div>

                    <hr className="m-t-30 m-b-30" />

                    <h6 className="m-b-20">{t('AGENTS_FORM_SECTION_ADDRESS_INFO')}</h6>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_ADDRESS')}</label>
                          <Input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_ADDRESS_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_CITY')}</label>
                          <Input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_CITY_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_COUNTRY')}</label>
                          <Input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_COUNTRY_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_POSTAL_CODE')}</label>
                          <Input
                            type="text"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_POSTAL_CODE_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                    </div>

                    <hr className="m-t-30 m-b-30" />

                    <h6 className="m-b-20">{t('AGENTS_FORM_SECTION_WEB_INFO')}</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_WEBSITE_URL')}</label>
                          <Input
                            type="url"
                            name="websiteUrl"
                            value={formData.websiteUrl}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_WEBSITE_URL_PLACEHOLDER')}
                          />
                          {validationErrors.websiteUrl && <small className="text-danger">{validationErrors.websiteUrl}</small>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>{t('AGENTS_FORM_FIELD_LOGO_URL')}</label>
                          <Input
                            type="url"
                            name="logoUrl"
                            value={formData.logoUrl}
                            onChange={handleChange}
                            placeholder={t('AGENTS_FORM_FIELD_LOGO_URL_PLACEHOLDER')}
                          />
                          {validationErrors.logoUrl && <small className="text-danger">{validationErrors.logoUrl}</small>}
                        </div>
                      </div>
                    </div>

                    <div className="row m-t-30">
                      <div className="col-md-12">
                        <button
                          type="submit"
                          className="btn btn-primary btn-round"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
                              {isEditMode ? t('AGENTS_FORM_BUTTON_UPDATING') : t('AGENTS_FORM_BUTTON_SAVING')}
                            </>
                          ) : (
                            <>
                              <i className="zmdi zmdi-check m-r-5"></i>
                              {isEditMode ? t('AGENTS_FORM_BUTTON_UPDATE') : t('AGENTS_FORM_BUTTON_SAVE')}
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-default btn-round m-l-10"
                          onClick={() => navigate('/agents')}
                          disabled={isSubmitting}
                        >
                          <i className="zmdi zmdi-close m-r-5"></i>
                          {t('AGENTS_FORM_BUTTON_CANCEL')}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </RoleBasedAccess>
  )
}

export default AgentFormPage

